<template>
    <!-- Floating install button — mobile only, when installable and not yet installed -->
    <button
        v-if="showFab"
        class="pwa-fab"
        type="button"
        @click="openModal"
    >
        <i class="bi bi-download me-1"></i> Install App
    </button>

    <!-- Install modal — teleported to body so it sits above everything -->
    <Teleport to="body">
        <Transition name="pwa-fade">
            <div v-if="showModal" class="pwa-backdrop" @click.self="dismissModal">
                <div class="pwa-card" role="dialog" aria-modal="true" :aria-label="`Install ${appName}`">
                    <img src="/logo.png" :alt="appName" class="pwa-icon" />

                    <h5 class="pwa-title">Install {{ appName }}</h5>
                    <p class="pwa-desc">
                        Install our app for a faster, offline-ready experience designed for your workflow.
                    </p>

                    <!-- iOS: no beforeinstallprompt — show manual instructions -->
                    <div v-if="isIos" class="pwa-ios-hint">
                        <i class="bi bi-box-arrow-up me-1"></i>
                        Tap <strong>Share</strong> &rarr; <strong>Add to Home Screen</strong>
                    </div>

                    <!-- Android / Desktop: use native install prompt -->
                    <template v-else>
                        <button class="btn btn-theme btn-sm w-100" @click="installApp">
                            <i class="bi bi-download me-1"></i> Install Now
                        </button>
                    </template>

                    <button class="btn btn-sm btn-outline-secondary w-100 mt-2" @click="dismissModal">
                        Maybe Later
                    </button>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, inject, onMounted, onUnmounted } from 'vue';

const emitter = inject('emitter');

// ── Constants ────────────────────────────────────────────────
const DISMISS_KEY  = 'pwa_dismissed_at';
const DISMISS_DAYS = 7;   // re-prompt after this many days

// ── State ────────────────────────────────────────────────────
const deferredPrompt = ref(null);   // beforeinstallprompt event (Chrome/Edge)
const isInstalled    = ref(false);  // running as standalone PWA
const isIos          = ref(false);  // iOS Safari (no beforeinstallprompt)
const isMobile       = ref(false);
const showModal      = ref(false);

// App name from blade window variable or document title
const appName = window.pwaAppName || document.title || 'HandyPro';

// ── Computed ─────────────────────────────────────────────────

// FAB: mobile only, not installed, prompt available (or iOS), modal not open
const showFab = computed(() =>
    !isInstalled.value &&
    isMobile.value &&
    (deferredPrompt.value !== null || isIos.value) &&
    !showModal.value
);

// ── Helpers ──────────────────────────────────────────────────
const wasDismissedRecently = () => {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    return (Date.now() - parseInt(ts)) / (1000 * 60 * 60 * 24) < DISMISS_DAYS;
};

const openModal   = () => { showModal.value = true; };
const dismissModal = () => {
    showModal.value = false;
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
};

const installApp = async () => {
    if (!deferredPrompt.value) return;
    deferredPrompt.value.prompt();
    const { outcome } = await deferredPrompt.value.userChoice;
    deferredPrompt.value = null;
    showModal.value = false;
    if (outcome === 'accepted') isInstalled.value = true;
};

// ── Event handlers ───────────────────────────────────────────

// Fires on Chrome/Edge (Android + desktop) when the site is installable
const onBeforeInstallPrompt = (e) => {
    e.preventDefault();
    deferredPrompt.value = e;
    // Auto-open modal on mobile if not dismissed recently
    if (isMobile.value && !wasDismissedRecently()) {
        setTimeout(() => { showModal.value = true; }, 1500);
    }
};

const onAppInstalled = () => {
    isInstalled.value    = true;
    deferredPrompt.value = null;
    showModal.value      = false;
};

// SW sends SW_UPDATED after a new service worker activates.
// We only listen when the SW was already controlling this page (i.e. not
// first-time install) so we don't cause an unwanted reload on first visit.
const onSwMessage = (event) => {
    if (event.data?.type === 'SW_UPDATED') {
        window.location.reload();
    }
};

// ── Lifecycle ────────────────────────────────────────────────
onMounted(() => {
    // Detect standalone (already installed)
    isInstalled.value =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;

    // Detect iOS
    isIos.value = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

    // Detect mobile
    isMobile.value = window.innerWidth < 768 || /android|iphone|ipad|ipod/i.test(navigator.userAgent);

    if (isInstalled.value) return; // already installed — nothing to do

    // iOS mobile: show install instructions if not dismissed
    if (isIos.value && isMobile.value && !wasDismissedRecently()) {
        setTimeout(() => { showModal.value = true; }, 1500);
    }

    // Listen for sidebar "Install App" click — force-open modal regardless of dismiss state
    emitter.on('open-pwa-install', openModal);

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    // SW update listener:
    // - If SW is already controlling (returning visitor) → listen immediately
    // - If not yet controlling (first visit) → wait for SW to become ready,
    //   then listen — the initial SW_UPDATED will have already been sent
    //   by then, so the first-visit reload is avoided.
    if (navigator.serviceWorker) {
        if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.addEventListener('message', onSwMessage);
        } else {
            navigator.serviceWorker.ready.then(() => {
                navigator.serviceWorker.addEventListener('message', onSwMessage);
            });
        }
    }
});

onUnmounted(() => {
    emitter.off('open-pwa-install', openModal);
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', onAppInstalled);
    navigator.serviceWorker?.removeEventListener('message', onSwMessage);
});
</script>

<style scoped>
/* ── Floating action button ─────────────────────────────────── */
.pwa-fab {
    position: fixed;
    bottom: 28px;
    right: 28px;
    z-index: 1050;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 50px;
    border: none;
    background: linear-gradient(135deg, var(--theme-primary, #013D82) 0%, #1a6fc4 100%);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    box-shadow: 0 4px 16px rgba(1, 61, 130, 0.35), 0 1px 3px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    animation: pwa-fab-pulse 2.5s ease-in-out infinite;
}
.pwa-fab:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 24px rgba(1, 61, 130, 0.4), 0 2px 6px rgba(0, 0, 0, 0.12);
    animation: none;
}

@keyframes pwa-fab-pulse {
    0%, 100% { box-shadow: 0 4px 16px rgba(1, 61, 130, 0.35), 0 1px 3px rgba(0, 0, 0, 0.1); }
    50% { box-shadow: 0 4px 24px rgba(1, 61, 130, 0.5), 0 1px 3px rgba(0, 0, 0, 0.1); }
}

/* On mobile, push FAB above the bottom navigation menu */
@media (max-width: 767.98px) {
    .pwa-fab {
        bottom: 80px;
    }
}

/* ── Backdrop ────────────────────────────────────────────────── */
.pwa-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1060;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

/* ── Modal card ──────────────────────────────────────────────── */
.pwa-card {
    background: #fff;
    border-radius: 18px;
    padding: 32px 24px 24px;
    max-width: 340px;
    width: 100%;
    text-align: center;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22);
}
.pwa-icon {
    width: 76px;
    height: 76px;
    border-radius: 18px;
    object-fit: cover;
    margin-bottom: 16px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14);
}
.pwa-title {
    font-size: 1.1rem;
    font-weight: 700;
    margin-bottom: 8px;
    color: #1a1a2e;
}
.pwa-desc {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 20px;
    line-height: 1.55;
}
.pwa-ios-hint {
    background: #f0f4ff;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 0.84rem;
    color: #444;
    margin-bottom: 16px;
    line-height: 1.5;
}

/* ── Transition ──────────────────────────────────────────────── */
.pwa-fade-enter-active,
.pwa-fade-leave-active {
    transition: opacity 0.2s ease;
}
.pwa-fade-enter-active .pwa-card,
.pwa-fade-leave-active .pwa-card {
    transition: transform 0.2s ease;
}
.pwa-fade-enter-from,
.pwa-fade-leave-to {
    opacity: 0;
}
.pwa-fade-enter-from .pwa-card {
    transform: translateY(30px);
}
</style>
